import { CommonTable } from "components/atoms/table/AntdTable";
import {
  BillToDatePresenterProps,
  UsageBreakDownDto,
} from "api/interfaces/planInterface.interface";
import { UsagePlan } from "pages/subscription/SubscriptionStyled";
import { FiFileText } from "react-icons/fi";
import * as s from "./BillToDateStyled";

const formatBillingMeta = (billingDate: string) => {
  const [year, month] = billingDate.split("-").map(Number);
  const d = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0).getDate();
  const monthLong = d.toLocaleString("en-US", { month: "long" });
  const monthShort = d.toLocaleString("en-US", { month: "short" });
  return {
    periodLabel: `${monthLong} ${year}`,
    estimatedDueDate: `${monthShort} ${lastDay}`,
  };
};

const formatBillingPeriod = (cycleStart: string, cycleEnd: string) => {
  const start = new Date(cycleStart);
  const end = new Date(cycleEnd);
  const startMonth = start.toLocaleString("en-US", { month: "short" });
  const endMonth = end.toLocaleString("en-US", { month: "short" });
  return `${startMonth} ${start.getDate()}, ${start.getFullYear()} - ${endMonth} ${end.getDate()}, ${end.getFullYear()}`;
};

const isCurrentMonth = (billingDate: string) => {
  const now = new Date();
  const [year, month] = billingDate.split("-").map(Number);
  return now.getFullYear() === year && now.getMonth() + 1 === month;
};

export default function BillToDatePresenter(props: BillToDatePresenterProps) {
  const { customer, billingDate, cycleStart, cycleEnd } = props.invoiceMeta;
  const { periodLabel, estimatedDueDate } = billingDate
    ? formatBillingMeta(billingDate)
    : { periodLabel: "", estimatedDueDate: "" };
  const billingPeriod =
    cycleStart && cycleEnd ? formatBillingPeriod(cycleStart, cycleEnd) : "";

  const isDraft = !!billingDate && isCurrentMonth(billingDate);

  const estimatedAmount = (
    (props.subscriptionTableProps.dataSource ?? []) as UsageBreakDownDto[]
  ).reduce((sum, row) => sum + row.amount, 0);

  return (
    <UsagePlan.Wrapper>
      <s.InvoiceCard>
        <s.Header>
          <s.TitleGroup>
            <div className="icon">
              <FiFileText />
            </div>
            <div className="text">
              <div className="title">
                {isDraft && "Draft"} Invoice - {periodLabel}
              </div>
              {isDraft && <div className="notice">NOT FINAL</div>}
            </div>
          </s.TitleGroup>
          {isDraft && <s.StatusBadge>DRAFT</s.StatusBadge>}
        </s.Header>

        <s.MetaGrid>
          <s.MetaItem>
            <span className="label">Customer</span>
            <span className="value">{customer}</span>
          </s.MetaItem>
          <s.MetaItem>
            <span className="label">Billing Period</span>
            <span className="value">{billingPeriod}</span>
          </s.MetaItem>
        </s.MetaGrid>

        <s.Section>
          <s.TableSkin>
            <s.SectionHeading>
              Usage Breakdown by Subscription Type
            </s.SectionHeading>
            <CommonTable {...props.subscriptionTableProps} />
          </s.TableSkin>
        </s.Section>

        <s.Section>
          <s.TableSkin className="usage-table">
            <s.SectionHeader>
              <s.SectionHeading>
                Channel Usage Breakdown by End User
              </s.SectionHeading>
              {props.isAbleExpand && (
                <s.ExpandButton onClick={props.onToggleExpandAll}>
                  {props.isAllExpanded ? "Collapse " : "Expand "}All
                </s.ExpandButton>
              )}
            </s.SectionHeader>
            <CommonTable {...props.usageTableProps} />
          </s.TableSkin>
        </s.Section>

        <s.Section>
          <s.TableSkin>
            <s.SectionHeading>
              Storage Usage Breakdown by End User
            </s.SectionHeading>
            <CommonTable {...props.storageTableProps} />
          </s.TableSkin>
        </s.Section>

        <s.AmountDue>
          <span className="label">
            Estimated Amount Due by {estimatedDueDate}
          </span>
          <span className="value">${estimatedAmount.toFixed(2)}</span>
        </s.AmountDue>
      </s.InvoiceCard>
    </UsagePlan.Wrapper>
  );
}
